"use client";

import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";
import * as React from "react";
import { cn } from "../../lib/utils";
import { Box } from "./box";
import { Icon } from "./icon";
import { Input } from "./input";
import { Text } from "./text";
import { buttonVariants } from "./button";

export function Breadcrumb({
  className,
  ...props
}: React.ComponentProps<"nav">): React.ReactElement {
  return (
    // `min-w-0`: seated as a flex item (a header row beside a search field),
    // the nav's automatic minimum is its list's min-content — the whole trail
    // unwrapped — so it refuses to shrink and the text runs under its
    // neighbour. Clearing it lets the list's own truncation take over.
    <nav
      aria-label="breadcrumb"
      className={cn("min-w-0", className)}
      data-slot="breadcrumb"
      {...props}
    />
  );
}

export function BreadcrumbList({
  className,
  ...props
}: React.ComponentProps<"ol">): React.ReactElement {
  return (
    // No gap between items: crumb spacing is carried by the separator's own
    // margin plus each crumb's own padding, so a hover wash butts flush
    // against the separator instead of floating in a gutter.
    //
    // Single line, no wrapping: the trail sits on a fixed-height header row, so
    // it absorbs a long name by truncating the page segment (BreadcrumbPage)
    // rather than reflowing onto a second line. Sibling link items keep their
    // intrinsic width.
    <ol
      className={cn(
        "flex min-w-0 items-center text-muted-foreground text-sm",
        className,
      )}
      data-slot="breadcrumb-list"
      {...props}
    />
  );
}

export function BreadcrumbItem({
  className,
  ...props
}: React.ComponentProps<"li">): React.ReactElement {
  return (
    <li
      // Only the item hosting the page segment (or the rename field that
      // stands in for it) may shrink below its content: that is what lets the
      // page text truncate. Every other item keeps the flex default of
      // `min-width: auto`, its own content width. A blanket `min-w-0` lets a
      // link or glyph-only crumb shrink in proportion to the page under a
      // long name, and its 24px face then overflows the shrunken item — the
      // "/" that follows lands on top of the glyph.
      className={cn(
        "inline-flex items-center gap-1.5 has-data-[slot=breadcrumb-page]:min-w-0 has-data-[slot=breadcrumb-input]:min-w-0",
        className,
      )}
      data-slot="breadcrumb-item"
      {...props}
    />
  );
}

// The crumb rung: Button's squared `xs` geometry with a 4px visual inset on
// both sides (3px + the 1px transparent border), a 4px glyph↔label gap, and
// 14px text at every viewport. Symmetric because the same rung dresses a
// text-only link, a glyph-only collapsed crumb and a glyph + label chip, and
// because BreadcrumbPage sits at the same 4px — so a "/" is the same distance
// from its neighbour whichever kind of crumb that is. `-mx-px` tucks the crumb
// into the separator's 2px margin so a hover wash butts flush against the
// slash. Squared, never the stadium: a crumb is a fragment of a trail, not a
// free-standing pill control, and its wash reads as text highlight.
const CRUMB_INSET_CLASS =
  "-mx-px [--button-gap:--spacing(1)] px-[calc(--spacing(1)-1px)] text-sm [&_svg:not([class*='size-'])]:size-4";

/** Button's ghost classes on the crumb rung, for any element in a trail. */
export function breadcrumbCrumbClass(ink: "muted" | "foreground" = "muted"): string {
  return cn(
    buttonVariants({ ink, shape: "square", size: "xs", variant: "ghost" }),
    CRUMB_INSET_CLASS,
  );
}

export function BreadcrumbLink({
  className,
  render,
  ...props
}: useRender.ComponentProps<"a">): React.ReactElement {
  // A crumb wears Button's ghost geometry on the crumb rung, so a link
  // crumb, an entity-chip crumb (EntityChipShell size="crumb"), and any
  // dropdown or icon-only crumb share one 24px/4px rhythm and their hover
  // washes sit flush against the "/" separators. Ink stays the list's muted
  // foreground at Button-free normal weight, brightening on hover.
  const defaultProps = {
    className: cn(
      breadcrumbCrumbClass(),
      "font-normal text-muted-foreground transition-colors hover:text-foreground",
      className,
    ),
    "data-slot": "breadcrumb-link",
  };

  return useRender({
    defaultTagName: "a",
    props: mergeProps<"a">(defaultProps, props),
    render,
  });
}

export function BreadcrumbPage({
  className,
  children,
  ...props
}: React.ComponentProps<"span">): React.ReactElement {
  return (
    // The current crumb keeps the crumb rhythm without the interactive chrome:
    // the trail's one 24px rung at every viewport, and the same effective text
    // inset as BreadcrumbLink's ghost wash (4px, tucked 1px into the
    // separator's 2px margin), so a "/" sits the same distance from every
    // neighbor whether that neighbor hovers or not. The rung never grows for
    // touch — the crumb Button takes its 44px target from the pointer-coarse
    // hit inflation, and this segment is not a press target at all.
    //
    // It is also the page header title across the app — 14px + semibold,
    // set apart from its sibling links by weight rather than size — and the
    // one segment that truncates, which is what `min-w-0` on the item buys.
    //
    // A flex row centering an inner ellipsis span, NOT a line-height-centered
    // inline-block: line-height centers only a raw text run, while call sites
    // also pass a nested Text or an adornment Box — block children that
    // top-align in a fixed-height inline-block and ride ~2px above the trail's
    // centerline. Flex centers every child shape identically; the inner span
    // supplies the text run `text-overflow` needs, so raw strings still
    // ellipsize.
    <span
      aria-current="page"
      className={cn(
        "-mx-px flex h-6 min-w-0 items-center rounded-md px-1 font-semibold text-sm text-foreground",
        className,
      )}
      data-slot="breadcrumb-page"
      {...props}
    >
      <span className="min-w-0 truncate">{children}</span>
    </span>
  );
}

export type BreadcrumbInputProps = {
  /** Name the field opens with. The field owns the draft from there — the
   *  host re-renders with the committed value once it lands. */
  defaultValue: string;
  /** Persist the trimmed draft. Never fires with an empty string, or with the
   *  value it opened on — both of those cancel instead. */
  onCommit: (next: string) => void;
  /** The edit ended without a change: Escape, an emptied field, or a blur that
   *  typed nothing new. */
  onCancel: () => void;
  /** Leading slot for the crumb's own visual (its glyph or avatar), held in
   *  place so the name doesn't shift when the edit begins. */
  visual?: React.ReactNode;
  maxLength?: number;
  "aria-label"?: string;
};

/**
 * The current crumb's inline-rename field: the same 24px rung, 14px semibold
 * type and visual slot as the crumb it replaces, so entering the edit moves
 * nothing — only a themed ring appears and the draft takes the brand ink.
 * Width follows the draft through a hidden sizer (the TabsTabInput grammar),
 * and every pointer/key event stops at the field so header gestures around
 * the trail never fire mid-edit.
 */
export function BreadcrumbInput({
  defaultValue,
  onCommit,
  onCancel,
  visual,
  maxLength = 60,
  "aria-label": ariaLabel,
}: BreadcrumbInputProps): React.ReactElement {
  const [draft, setDraft] = React.useState(defaultValue);
  // Enter and Escape both end the edit and drop focus, and the blur that
  // follows would otherwise resolve it a second time.
  const settledRef = React.useRef(false);

  const settle = React.useCallback(
    (commit: boolean) => {
      if (settledRef.current) return;
      settledRef.current = true;
      const next = draft.trim();
      if (commit && next && next !== defaultValue) onCommit(next);
      else onCancel();
    },
    [draft, defaultValue, onCommit, onCancel],
  );

  const stop = React.useCallback((event: React.SyntheticEvent) => {
    event.stopPropagation();
  }, []);

  return (
    <Box
      align="center"
      direction="row"
      rounded="md"
      // The trail's one 24px rung at every viewport and its -1px tuck into the
      // separator margin, plus the active crumb's 14px/600 type — the sizer
      // and the field both inherit it, so the swap moves nothing. The field
      // opens focused, so it needs no touch target of its own: the press that
      // reaches it is on the crumb Button it replaces, which carries the
      // pointer-coarse hit inflation.
      className="-mx-px h-6 font-semibold text-sm inset-ring-1 inset-ring-ring"
      data-slot="breadcrumb-input"
      onClick={stop}
      onPointerDown={stop}
      onKeyDown={(event: React.KeyboardEvent) => {
        event.stopPropagation();
        if (event.key === "Enter") settle(true);
        if (event.key === "Escape") settle(false);
      }}
    >
      {visual ? (
        // The crumb's 4px visual inset; the field's own 4px sizer padding is
        // the glyph↔label gap, matching the crumb's [--button-gap].
        <Box align="center" direction="row" shrink={false} className="ms-1">
          {visual}
        </Box>
      ) : null}
      <Box
        align="center"
        direction="row"
        maxWidth={288}
        minWidth={40}
        overflow="hidden"
        position="relative"
      >
        {/* Width driver only — the same glyphs, unpainted, in the flow. */}
        <Text
          aria-hidden
          className="pointer-events-none whitespace-pre px-1 font-[inherit] text-[inherit] opacity-0"
        >
          {draft || " "}
        </Text>
        {/* `bare` and `[font:inherit]`: the field wears the crumb's own type
            and owns no metrics of its own; the draft takes the brand ink so
            the edit reads as themed, not just focused. */}
        <Input
          aria-label={ariaLabel}
          autoFocus
          bare
          className="absolute inset-0 h-full w-full min-w-0 bg-transparent px-1 text-brand outline-none [font:inherit]"
          maxLength={maxLength}
          onBlur={() => settle(true)}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            setDraft(event.target.value)
          }
          onFocus={(event: React.FocusEvent<HTMLInputElement>) =>
            event.currentTarget.select()
          }
          value={draft}
        />
      </Box>
    </Box>
  );
}

export function BreadcrumbSeparator({
  children,
  className,
  ...props
}: React.ComponentProps<"li">): React.ReactElement {
  // A "/" at the list's own 14px, quieter than the labels, with 2px side
  // margins — the glyph's side bearing plus this margin is the whole gap to
  // the neighboring crumbs (the list itself has none).
  return (
    <li
      aria-hidden="true"
      className={cn("mx-0.5 select-none opacity-80 [&>svg]:size-4", className)}
      data-slot="breadcrumb-separator"
      role="presentation"
      {...props}
    >
      {children ?? "/"}
    </li>
  );
}

export function BreadcrumbEllipsis({
  className,
  ...props
}: React.ComponentProps<"span">): React.ReactElement {
  return (
    // The collapsed-trail marker keeps the crumb rhythm (the trail's one 24px
    // rung at every viewport, 4px inset, tucked 1px into the separator
    // margins) without the interactive chrome, and being decoration it is
    // never a press target, so it takes no touch inflation either.
    <span
      aria-hidden="true"
      className={cn(
        "-mx-px flex h-6 items-center rounded-md px-1",
        className,
      )}
      data-slot="breadcrumb-ellipsis"
      role="presentation"
      {...props}
    >
      <Icon className="size-4" name="ellipsis" />
      <span className="sr-only">More</span>
    </span>
  );
}
